import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { getDocument } from '../firebase/firestore';
import Loader from '../components/common/Loader';
import Button from '../components/common/Button';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchServiceDetail();
  }, [serviceId]);

  const fetchServiceDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { db } = await import('../firebase/config');
      
      if (!db) {
        setError('Database not configured');
        setLoading(false);
        return;
      }

      // Try to fetch by document ID first
      let serviceData = await getDocument('services', serviceId);
      
      // If not found by ID, try to find by slug
      if (!serviceData) {
        const { getCollection } = await import('../firebase/firestore');
        const allServices = await getCollection('services');
        serviceData = allServices.find(s => s.slug === serviceId);
        
        if (serviceData) {
          // Update the service object with the found data
          setService(serviceData);
        } else {
          setError('Service not found');
        }
      } else {
        setService(serviceData);
      }
    } catch (err) {
      console.error('Error fetching service:', err);
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="service-detail-loading">
        <Loader />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-error">
        <div className="container">
          <div className="error-content">
            <h1>Service Not Found</h1>
            <p>{error || 'The service you are looking for does not exist.'}</p>
            <Link to="/services">
              <Button>
                <FaArrowLeft /> Back to Services
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Hero Section */}
      <section className="service-hero">
        {service.mainImage && (
          <div className="service-hero-image">
            <img src={service.mainImage} alt={service.title} />
          </div>
        )}
        <div className="service-hero-overlay"></div>
        <div className="container">
          <div className="service-hero-content">
            <button onClick={() => navigate(-1)} className="back-button">
              <FaArrowLeft /> Back
            </button>
            <h1>{service.title}</h1>
            {service.pricing && (
              <div className="service-pricing">
                <span className="pricing-label">Starting at</span>
                <span className="pricing-value">{service.pricing}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-content">
        <div className="container">
          <div className="service-content-grid">
            {/* Main Description */}
            <div className="service-main">
              {service.detailedDescription && (
                <div className="service-description">
                  <p>{service.detailedDescription}</p>
                </div>
              )}

              {/* Features */}
              {service.features && service.features.length > 0 && (
                <div className="service-features">
                  <h2>Key Features</h2>
                  <ul className="features-list">
                    {service.features.map((feature, index) => (
                      <li key={index}>
                        <FaCheckCircle className="feature-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Additional Sections */}
              {service.sections && service.sections.length > 0 && (
                <div className="service-sections">
                  {service.sections.map((section, index) => (
                    <div key={index} className="service-section" data-aos="fade-up">
                      <h2>{section.title}</h2>
                      <p>{section.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="service-sidebar">
              <div className="service-cta-card">
                <h3>Ready to Get Started?</h3>
                <p>Contact us today to learn more about this service and how we can help your business grow.</p>
                <Link to="/contact">
                  <Button size="large" className="cta-button-full">
                    Contact Us Now →
                  </Button>
                </Link>
              </div>

              {service.image && (
                <div className="service-sidebar-image">
                  <img src={service.image} alt={service.title} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
